import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useToast } from '@/hooks/useToast';
import type { Equipamento } from '../../../../models';
import { recuperarHistoricoEquipamento, type PossivelMovimentacaoDoEquipamento } from '../../../../services';
import { formatarData } from './ativoHistorico.format';

/**
 * "Buscar histórico nas Solicitações" — o próprio SI traz o passado do ativo (Movimentações
 * e Estadias antigas), sem depender de script. O que só bate por patrimônio/nº série, sem
 * vínculo, não entra sozinho: aparece aqui pra confirmar.
 */
export default function RecuperarHistoricoBarra({ equipamento }: { equipamento: Equipamento }) {
  const toast = useToast();
  const [buscando, setBuscando] = useState(false);
  const [possiveis, setPossiveis] = useState<PossivelMovimentacaoDoEquipamento[] | null>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());

  const buscar = async (confirmarMovimentacaoIds?: string[]) => {
    setBuscando(true);
    try {
      const resultado = await recuperarHistoricoEquipamento({ equipamentoId: equipamento.id, confirmarMovimentacaoIds });
      setPossiveis(resultado.possiveis);
      setMarcados(new Set());
      toast.success(
        resultado.recuperados > 0
          ? `${resultado.recuperados} ${resultado.recuperados === 1 ? 'evento recuperado' : 'eventos recuperados'}.`
          : 'Nenhum evento anterior encontrado pelo vínculo.'
      );
    } catch (error: any) {
      toast.error(`Não foi possível buscar o histórico: ${error?.message || 'erro desconhecido'}`);
    } finally {
      setBuscando(false);
    }
  };

  const alternar = (id: string) =>
    setMarcados((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });

  return (
    <div className="mb-4 space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex-1 text-sm text-gray-600">
          {equipamento.historicoRecuperadoEm
            ? `Histórico anterior buscado em ${formatarData(equipamento.historicoRecuperadoEm)}.`
            : 'Eventos de antes do histórico existir (Solicitações e Estadias antigas) ainda não foram buscados.'}
        </p>
        <button type="button" className="btn-secondary flex items-center gap-1.5 text-sm" disabled={buscando} onClick={() => buscar()}>
          <ArrowPathIcon className={buscando ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          {equipamento.historicoRecuperadoEm ? 'Buscar novamente' : 'Buscar histórico nas Solicitações'}
        </button>
      </div>

      {possiveis && possiveis.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
          <p className="font-medium text-amber-900">
            Movimentações que podem ser deste ativo (sem vínculo, só pelo mesmo patrimônio ou nº de série). Marque as que são:
          </p>
          <ul className="mt-2 space-y-1">
            {possiveis.map((p) => (
              <li key={p.movimentacaoId} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                  checked={marcados.has(p.movimentacaoId)}
                  onChange={() => alternar(p.movimentacaoId)}
                />
                <span className="flex-1 text-gray-700">
                  {p.tipoFluxo === 'devolucao' ? 'Devolução' : 'Envio'} · {p.colaboradorNome ?? 'colaborador não informado'}
                  {p.data ? ` · ${new Date(p.data).toLocaleDateString('pt-BR')}` : ''} · mesmo {p.casamento}
                </span>
                <Link to={`/si/solicitacoes/${p.solicitacaoId}`} target="_blank" className="text-xs text-blue-600 hover:underline">
                  ver
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              className="btn-primary text-sm"
              disabled={marcados.size === 0 || buscando}
              onClick={() => buscar(Array.from(marcados))}
            >
              Adicionar as marcadas ao histórico
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
